import { Box, Button, Stack, SxProps, Typography } from '@mui/material';
import { useState } from 'react';
import Dialog from '../Dialog/Dialog';
import DialogTitle from '../DialogTitle/DialogTitle';
import InfoIcon from '../../assets/icons/Info';
import DialogContent from '../DialogContent/DialogContent';
import DialogActions from '../DialogActions/DialogActions';
import {
  ActionsSx,
  CancelButtonSx,
  ConfirmButtonSx,
  ContentSx,
  EmptyContentSx,
  ErrorMessageSx,
  InfoIconSx,
  PaperSx,
  TitleSx,
} from './ConfirmDialogProps';

type Props = {
  open: boolean;
  title: string | JSX.Element;
  content?: string | JSX.Element;
  cancelButton?: {
    text: string;
    onClick: () => unknown;
    dataTestId?: string;
  };
  confirmButton: {
    text: string;
    onClick: () => unknown;
    skipCloseCallback?: boolean;
    dataTestId?: string;
  };
  errorMessage?: string;
  onClose: () => unknown;
  sx?: SxProps;
  state?: 'information' | 'error';
  emphasizeCancel?: boolean;
};

const ConfirmationDialog = ({
  open,
  title,
  content,
  cancelButton,
  confirmButton,
  errorMessage,
  onClose,
  sx,
  state = 'information',
  emphasizeCancel = false,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await confirmButton.onClick();
      if (!confirmButton.skipCloseCallback) {
        onClose();
      }
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, _reason) => onClose()}
      disableBackdropClick={false}
      paperSx={{ ...PaperSx, ...sx }}
    >
      <DialogTitle>
        <Stack flexDirection={'column'} alignItems={'center'}>
          <InfoIcon sx={InfoIconSx(state)} />
          <Typography sx={TitleSx} textAlign={'center'}>
            {title}
          </Typography>
        </Stack>
      </DialogTitle>
      {content ? (
        <DialogContent>
          <Typography sx={ContentSx} textAlign={'center'}>
            {content}
          </Typography>
        </DialogContent>
      ) : (
        <Box sx={EmptyContentSx} />
      )}
      <DialogActions sx={ActionsSx}>
        {cancelButton && (
          <Button
            variant={emphasizeCancel ? 'contained' : 'outlined'}
            onClick={cancelButton.onClick}
            sx={emphasizeCancel ? ConfirmButtonSx(state) : CancelButtonSx}
            data-testid={cancelButton.dataTestId}
          >
            {cancelButton.text}
          </Button>
        )}

        <Button
          variant={emphasizeCancel ? 'outlined' : 'text'}
          onClick={handleConfirm}
          sx={emphasizeCancel ? CancelButtonSx : ConfirmButtonSx(state)}
          data-testid={confirmButton.dataTestId}
          disabled={isLoading}
        >
          {confirmButton.text}
        </Button>
      </DialogActions>
      {errorMessage && (
        <Typography sx={ErrorMessageSx}>{errorMessage}</Typography>
      )}
    </Dialog>
  );
};

export default ConfirmationDialog;
